import React from 'react';
import vars from '@variables';
import styled from '@emotion/styled/macro';
import ErrorLayout from '@layouts/error-layout';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import SecondaryNav from '@components/secondary-nav/secondary-nav';
import FlexibleContentHandler from '../design-system/src/components/flexible-content-handler/flexible-content-handler';
import TileBlock from '@blocks/tile-block/tile-block';
import FeaturedCircularExampleBlock from '@blocks/featured-circular-example-block/featured-circular-example-block';
import AboutUsBlock from '@blocks/about-us-block/about-us-block';
import HomepageMasthead from '@components/homepage-masthead/homepage-masthead';
import { graphql } from 'gatsby';
import { generateHomePageLocaleUrls } from '../utils/generate-url-functions';
import { getPageColour } from '../config/cms-mapping-functions';
import { getFooterData } from '../utils/translation-functions';
import {
  transformFlexibleContent,
  transformHomepageContent,
  getHomePageHrefLangUrls,
} from '../utils/transform-data-functions.js';
import { validateHomePageData } from '../utils/validate-cms-data-functions';

const StyledWrapper = styled.div`
  .main-container {
    display: flex;
    max-width: ${vars.mainMaxWidth};
    justify-content: center;
    margin: 0 auto;

    .main-content-container {
      min-width: ${vars.contentMinWidth};
      width: calc(
        ${vars.contentMaxWidth} + calc(2 * ${vars.standardColumnGap})
      );
      padding: 0 ${vars.mainContentContainerPaddingMobile} ${vars.spacingL};

      @media screen and (min-width: ${vars.breakPointTenColumn}) {
        padding: 0 ${vars.mainContentContainerPaddingTenColumn} ${vars.spacingL};
      }

      @media screen and (min-width: ${vars.breakPointTablet}) {
        padding: 0 ${vars.mainContentContainerPaddingTenColumn} ${vars.spacingL};
      }

      @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
        padding: 0 ${vars.spacingL} 12rem ${vars.spacingL};
      }
    }
  }
`;

export const query = graphql`
  query HomepageQuery($id: String, $podcastSlugs: [String]) {
    contentfulHomePage(id: { eq: $id }) {
      ...HomepageFields
    }
    allSimplecastPodcastEpisode(filter: { slug: { in: $podcastSlugs } }) {
      nodes {
        id
        slug
      }
    }
    contentfulGlossaryManager(enabled: { eq: true }) {
      termsForTooltip {
        label
        definition {
          definition
        }
        definitionImage {
          gatsbyImageData(
            placeholder: BLURRED
            formats: [AUTO, AVIF, WEBP]
            quality: 80
          )
          description
          file {
            url
            contentType
          }
        }
        linkType
        externalLink
        internalLink {
          ...StandardPageReference
          ...SubPageReference
          ...CircularExamplePageReference
          ...ResourcePageReference
          ...WebflowPageReference
        }
      }
    }
  }
`;

const HomePage = ({ data, pageContext, className }) => {
  const { contentfulHomePage } = data;

  const pageData = contentfulHomePage;

  const pageValidation = validateHomePageData(pageData);

  // Primary nav and footer data handling
  const footerData = getFooterData(pageContext.locale);

  if (pageValidation.isValid) {
    const transformedData = transformHomepageContent(
      pageData,
      pageContext.locale,
      pageContext.staticTranslations
    );

    const localeLinks = generateHomePageLocaleUrls();
    const hreflangUrls = getHomePageHrefLangUrls(localeLinks);

    const metaData = {
      title: pageData.masthead.title,
      metaTitle: pageData.metaTitle || pageData.masthead.title,
      metaDescription:
        pageData.metaDescription || pageData.masthead.description,
      socialCardDescription:
        pageData.metaDescription || pageData.masthead.description,
      canonicalLink: pageData.canonicalLink,
      localeCode: pageContext.locale,
    };

    const gtmDataLayerVars = {
      pageId: pageContext.contentfulId,
      event: 'Page View',
      pageType: 'home page',
      updatedAt: pageData.updatedAt,
    };

    const simplecastData = data.allSimplecastPodcastEpisode?.nodes;
    const { termsForTooltip } = data.contentfulGlossaryManager;

    const rawTooltipData = {
      termsForTooltip,
      shouldDisplayTextTooltips: pageData.shouldDisplayTextTooltips,
      usedTooltipKeywords: [],
    };

    const transformedFlexibleContent = pageData.flexibleContent
      ? transformFlexibleContent({
          flexibleContent: pageData.flexibleContent,
          localeCode: pageContext.locale,
          staticTranslations: pageContext.staticTranslations,
          pageColour: getPageColour(),
          simplecastData,
          rawTooltipData,
          currentEntryId: pageContext.id,
        })
      : null;

    if (transformedData) {
      return (
        <Layout
          localeLinks={localeLinks}
          footerData={footerData}
          displayBorders={false}
          className={className}
          metaData={metaData}
          gtmDataLayerVars={gtmDataLayerVars}
          hreflangUrls={hreflangUrls}
          globals={pageContext?.globals ? pageContext.globals : null}
          hasTopPadding={true}
          navbarInitialNoBackground={true}
          navbarInitialLightText={true}
        >
          <HomepageMasthead masthead={transformedData.masthead} />
          <StyledWrapper>
            <div className="main-container">
              <div className="main-content-container">
                {transformedFlexibleContent && (
                  <FlexibleContentHandler data={transformedFlexibleContent} />
                )}
              </div>
            </div>
          </StyledWrapper>
        </Layout>
      );
    } else {
      console.error('Home Page not rendered - required data is missing.');
      return null;
    }
  } else {
    return (
      <ErrorLayout footerData={footerData} errors={pageValidation.errors} />
    );
  }
};

const StyledHomePage = styled(HomePage)`
  ${SecondaryNav} {
    margin-top: ${vars.spacingM};
    margin-bottom: ${vars.spacingS};
  }
  .secondary-nav-separator {
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      position: absolute;
      left: 0;
      right: 0;
      width: 100%;
      border-bottom: solid ${vars.transparentBorderGrey} 1px;
    }
  }

  ${SectionLayout} {
    ${TileBlock} {
      margin: 3rem 0;
      h2 {
        border: none;
        padding-left: 0;
        margin-left: 0;
      }
      &:first-of-type {
        margin-top: 5rem;
      }
      &:last-of-type {
        margin-bottom: 3rem;
        @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
          margin-bottom: 5rem;
        }
      }
    }
    ${FeaturedCircularExampleBlock} {
      margin-bottom: 3rem;
      @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
        margin-bottom: 15rem;
      }
    }
    ${AboutUsBlock} {
      /* Add local styling if needed */
    }
  }

  .link-destination {
    /* @TO-DO: make the following dynamic based on main nav height */
    margin-top: 0;
  }
  .widget {
    width: 100%;
    overflow: hidden;

    margin-top: 5rem;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      margin-top: 0;
    }
    h2 {
      border: none;
      padding: 0;
      margin-left: 0;
    }
  }
`;

export default StyledHomePage;
